import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, ShoppingBag, Sparkles, Search, Instagram } from "lucide-react";

const TITLE = "What Is a Creator Storefront? A Shopping Guide";
const DESCRIPTION =
  "What creator storefronts are, how to find them on Amazon, Instagram, and Pluggz, and why two-tap brand checkout matters when you shop a creator's picks.";
const PATH = "/journal/guide-to-creator-storefronts";

export const Route = createFileRoute("/journal_/guide-to-creator-storefronts")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-08",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is a creator storefront?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A creator storefront is a personal, curated shop where a creator gathers the products they actually use and recommend in one place, earning a small affiliate commission when you buy through their links.",
              },
            },
            {
              "@type": "Question",
              name: "How do I find a creator's Amazon storefront?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Go to amazon.com/shop/<creator-handle>, or search the creator's name plus 'Amazon storefront'. Most creators also link to it from their Instagram, TikTok, or YouTube bio.",
              },
            },
            {
              "@type": "Question",
              name: "How is a Pluggz storefront different from an Amazon storefront?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Pluggz storefronts are editorial and cross-brand: every creator is hand-vetted, and checkout happens on the brand's own site in two taps — not inside a single retailer.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: "Guide to creator storefronts", item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: GuidePage,
});

function GuidePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav
        aria-label="Breadcrumb"
        className="relative mx-auto max-w-3xl px-6 pt-10"
      >
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li>
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          </li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li>
            <Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link>
          </li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Guide to creator storefronts</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Guide · 6 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            What is a <span className="italic shimmer">creator storefront</span>, and how do you find one worth shopping?
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mx-auto mt-6 max-w-xl text-base text-muted-foreground">
            A plain-English guide to creator-led shopping in 2026 — what storefronts actually are, where to find them across Amazon, Instagram, and Pluggz, and why two-tap brand checkout changes the experience.
          </p>
        </Reveal>
      </header>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing">
          <Reveal>
            <p className="text-lg leading-relaxed">
              You've probably clicked one without realising it. A favourite creator posts an outfit, a serum, a ceramic vase — and a tap later you're on a tidy little page with twelve things they swear by. That page is a <strong>creator storefront</strong>: a personal, curated shop where a creator gathers the products they actually use, earns a small affiliate commission when you buy, and saves you the work of hunting through a feed for the exact link.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <h2 className="mt-12 font-serif text-3xl">A short, useful definition</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              A creator storefront is one creator's edit of products — usually 10 to 200 items — presented on a single shoppable page. You're not buying <em>from</em> the creator; you're buying from the brand or retailer behind each link. The creator earns affiliate commission, you get a vetted shortlist, and nothing else in the transaction changes.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="mt-12 font-serif text-3xl">Where to find them</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Creator storefronts live in three main places in 2026. Each has its own trade-offs.
            </p>

            <div className="mt-6 grid gap-4">
              <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur">
                <div className="flex items-center gap-3">
                  <Search className="h-4 w-4 text-accent" />
                  <h3 className="font-serif text-xl">Amazon storefronts</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  The most common entry point. Visit <code className="rounded bg-muted px-1.5 py-0.5 text-[12px]">amazon.com/shop/&lt;creator-handle&gt;</code>, or search the creator's name plus "Amazon storefront". You'll find their idea lists, livestream replays, and recommended categories — but every item is, by definition, sold by Amazon.
                </p>
              </div>

              <div className="rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur">
                <div className="flex items-center gap-3">
                  <Instagram className="h-4 w-4 text-accent" />
                  <h3 className="font-serif text-xl">Instagram & TikTok shops</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Tap the shopping bag icon on a creator's profile, or follow the link in their bio (usually LTK, ShopMy, or Beacons). These pages aggregate links across many brands, but the checkout experience often pushes you through multiple redirects before you reach the actual product.
                </p>
              </div>

              <div className="rounded-2xl border-2 border-foreground/80 bg-background p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="h-4 w-4" />
                  <h3 className="font-serif text-xl">Pluggz storefronts</h3>
                  <span className="ml-auto rounded-full bg-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest text-background">Our approach</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Editorial, cross-brand storefronts from <strong>vetted</strong> creators only — 50 to 150 of them at any time. Every link goes straight to the brand's own checkout, so you're paying the brand directly, with their returns and their warranty. Two taps from the feed to the brand cart.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="mt-12 font-serif text-3xl">Why "two-tap checkout" matters</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              The biggest friction in creator shopping isn't discovery — it's the path from "I want it" to "it's mine". A typical affiliate link in 2026 can route through three or four redirects: link shortener → aggregator page → out-of-stock retailer → brand site. By the time you arrive, the product has changed price, sold out, or replaced itself with a similar-but-not-quite version.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Pluggz is built around a single rule: two taps. Tap the product, tap "buy", you're on the brand's checkout. The creator still earns commission — the affiliate attribution is baked into the redirect — but you skip the maze.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <h2 className="mt-12 font-serif text-3xl">How to spot a storefront worth shopping</h2>
            <ul className="mt-4 space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                <span><strong className="text-foreground">It feels edited, not endless.</strong> Twenty things the creator genuinely uses beats a thousand items pulled from an algorithm.</span>
              </li>
              <li className="flex gap-3">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                <span><strong className="text-foreground">The creator says <em>why</em>.</strong> A short note next to each product — "I've reordered this three times" — tells you it's lived in, not just listed.</span>
              </li>
              <li className="flex gap-3">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                <span><strong className="text-foreground">Checkout is on the brand's site.</strong> Pay the brand directly so returns, sizing, and warranty stay simple.</span>
              </li>
              <li className="flex gap-3">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                <span><strong className="text-foreground">Disclosure is visible.</strong> A trustworthy storefront tells you it earns affiliate commission. That transparency is the point.</span>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
              <p className="editorial-eyebrow">Start browsing</p>
              <h3 className="mt-3 font-serif text-3xl">Find a creator whose taste you trust.</h3>
              <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                Pluggz is a curated marketplace of editorial storefronts from vetted creators. Two taps to the brand's checkout.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
                <Link to="/creators" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                  Browse live creators
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
                <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">
                  Open the discovery feed
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-10 text-xs text-muted-foreground">
              Published June 2026 · Pluggz Journal
            </p>
          </Reveal>
        </section>
      </div>
    </article>
  );
}
